import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tour } from "antd";
import { useLocation } from "react-router-dom";

import { getTourForPath } from "./tourSteps";
import styles from "./GlobalProductTour.module.css";

export const PRODUCT_TOUR_START_EVENT = "uprasti:start-product-tour";

// Сколько ждать, пока страница дорисует то, на что показывают подсказки.
const READY_ATTEMPTS = 20;
const READY_INTERVAL_MS = 250;

function findVisibleElement(selector, text) {
  if (!selector || typeof document === "undefined") return null;

  const normalizedText = String(text || "").trim().toLowerCase();
  const elements = Array.from(document.querySelectorAll(selector)).filter(
    (element) => {
      if (!normalizedText) return true;

      return String(element.textContent || "")
        .trim()
        .toLowerCase()
        .includes(normalizedText);
    }
  );

  return (
    elements.find((element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        style.opacity !== "0"
      );
    }) || null
  );
}

// Шаг, чей элемент не нашёлся на экране, пропускаем: подсказка
// не должна показывать на пустое место.
function resolveSteps(tour) {
  return tour.steps
    .filter((item) => !item.selector || findVisibleElement(item.selector, item.text))
    .map(({ selector, text, ...item }) =>
      selector ? { ...item, target: () => findVisibleElement(selector, text) } : item
    );
}

// Подсказки «Как пользоваться». Сами не открываются: только по событию
// PRODUCT_TOUR_START_EVENT из «Помощь › Как пользоваться» или кнопки «?».
// Обзор меню при первом входе показывает само меню (navV3/NavV3Tour.jsx).
export default function GlobalProductTour() {
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [steps, setSteps] = useState([]);

  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    clearTimer();
    setOpen(false);
    setCurrent(0);
  }, [clearTimer]);

  const startTour = useCallback(() => {
    clearTimer();

    const tour = getTourForPath(location.pathname, location.search);
    let attempt = 0;

    const tryStart = () => {
      const ready = !tour.ready || Boolean(findVisibleElement(tour.ready));

      if (!ready && attempt < READY_ATTEMPTS) {
        if (attempt === 0) {
          setSteps([{ title: "Готовим подсказки", description: "Дождитесь загрузки страницы. Подсказки появятся здесь." }]);
          setCurrent(0);
          setOpen(true);
        }
        attempt += 1;
        timerRef.current = setTimeout(tryStart, READY_INTERVAL_MS);
        return;
      }

      const nextSteps = resolveSteps(tour);
      if (nextSteps.length === 0) nextSteps.push({
        title: "Подсказки пока не удалось показать",
        description: "Дождитесь загрузки страницы и попробуйте ещё раз. Если не помогло, откройте «Помощь → Написать в поддержку».",
      });

      setSteps(nextSteps);
      setCurrent(0);
      setOpen(true);
    };

    tryStart();
  }, [clearTimer, location.pathname, location.search]);

  // Ушли на другую страницу — подсказки прошлой страницы закрываем.
  useEffect(() => {
    close();
  }, [location.pathname, location.search, close]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    const handleStartTour = (event) => {
      const requestedPathname = event?.detail?.pathname;
      if (requestedPathname && requestedPathname !== location.pathname) return;

      startTour();
    };

    window.addEventListener(PRODUCT_TOUR_START_EVENT, handleStartTour);
    return () => window.removeEventListener(PRODUCT_TOUR_START_EVENT, handleStartTour);
  }, [location.pathname, startTour]);

  useEffect(() => clearTimer, [clearTimer]);

  return (
    <Tour
      rootClassName={styles.tour}
      open={open}
      current={current}
      steps={steps}
      onChange={setCurrent}
      onFinish={close}
      onClose={close}
      // Лёгкое затемнение: подсвечиваем место, но страницу не прячем.
      mask={{ color: "rgba(15, 23, 42, 0.25)" }}
      arrow={{ pointAtCenter: true }}
      gap={{ offset: 8, radius: 12 }}
      scrollIntoViewOptions={{ behavior: "smooth", block: "center", inline: "center" }}
      zIndex={100000}
    />
  );
}
